import { DqLoader } from "@decentriq/components";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  ModalDialog,
} from "@mui/joy";
import saveAs from "file-saver";
import { memo, useEffect } from "react";
import { useMediaDataRoom } from "features/mediaDataRoom/contexts";
import { type MediaDataRoomJobHookResult } from "features/mediaDataRoom/hooks";
import { mapMediaDataRoomErrorToSnackbar, useDataRoomSnackbar } from "hooks";
import MediaDataRoomVersionSwitch from "../MediaDataRoomVersionSwitch/MediaDataRoomVersionSwitch";
import { default as useModelPerformanceReportAbGeneration } from "./hooks/ab/useModelPerformanceReportGeneration";
import { default as useModelPerformanceReportV3Generation } from "./hooks/v3/useModelPerformanceReportGeneration";

interface PerformanceReportDownloadDialogProps {
  dataRoomId: string;
  open: boolean;
  onCancel: () => void;
}

interface SharedPerformanceReportDownloadDialogProps {
  jobResult: MediaDataRoomJobHookResult<string>;
  onCancel: () => void;
  open: boolean;
}

const PerformanceReportDownloadDialog =
  memo<SharedPerformanceReportDownloadDialogProps>(
    ({ jobResult: { error, computeResults, loading }, open, onCancel }) => {
      const { enqueueSnackbar } = useDataRoomSnackbar();
      useEffect(() => {
        if (!open || loading) return;
        if (error) {
          enqueueSnackbar(
            ...mapMediaDataRoomErrorToSnackbar(
              error,
              "Unable to fetch performance report"
            )
          );
          onCancel();
          return;
        }
        if (!computeResults) return;
        const file = new File(
          [JSON.stringify(computeResults, null, 2)],
          `Performance_report_${new Date().toISOString()}.json`,
          {
            type: "application/octet-stream;charset=utf-8",
          }
        );
        saveAs(file);
        onCancel();
      }, [computeResults, enqueueSnackbar, error, loading, onCancel, open]);
      return (
        <Modal onClose={onCancel} open={open}>
          <ModalDialog>
            <DialogTitle>Preparing performance report</DialogTitle>
            <Divider />
            <DialogContent>
              <DqLoader
                size="md"
                sx={{ justifyContent: "center", width: "100%" }}
              />
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button onClick={onCancel}>Close</Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
      );
    }
  );
PerformanceReportDownloadDialog.displayName = "PerformanceReportDownloadDialog";

const PerformanceReportAbDownloadDialog =
  memo<PerformanceReportDownloadDialogProps>(({ dataRoomId, ...restProps }) => {
    const { driverAttestationHash } = useMediaDataRoom();
    const jobResult = useModelPerformanceReportAbGeneration({
      dataRoomId,
      driverAttestationHash,
      skip: !restProps.open,
    });
    return (
      <PerformanceReportDownloadDialog jobResult={jobResult} {...restProps} />
    );
  });
PerformanceReportAbDownloadDialog.displayName =
  "PerformanceReportAbDownloadDialog";

const PerformanceReportV3DownloadDialog =
  memo<PerformanceReportDownloadDialogProps>(({ dataRoomId, ...restProps }) => {
    const jobResult = useModelPerformanceReportV3Generation({
      dataRoomId,
      skip: !restProps.open,
    });
    return (
      <PerformanceReportDownloadDialog jobResult={jobResult} {...restProps} />
    );
  });
PerformanceReportV3DownloadDialog.displayName =
  "PerformanceReportV3DownloadDialog";

const PerformanceReportV3DownloadDialogVersioned =
  memo<PerformanceReportDownloadDialogProps>((props) => (
    <MediaDataRoomVersionSwitch
      ab={<PerformanceReportAbDownloadDialog {...props} />}
      v3={<PerformanceReportV3DownloadDialog {...props} />}
    />
  ));
PerformanceReportV3DownloadDialogVersioned.displayName =
  "PerformanceReportV3DownloadDialogVersioned";

export default PerformanceReportV3DownloadDialogVersioned;
